import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, unref as _unref, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  id: "leaderboard",
  class: "section-container"
}
const _hoisted_2 = { class: "section-content" }
const _hoisted_3 = { class: "title-row" }
const _hoisted_4 = { class: "view-switcher" }
const _hoisted_5 = { class: "section-lead" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { class: "tools" }
const _hoisted_9 = { class: "table-container" }
const _hoisted_10 = {
  key: 0,
  class: "score-value"
}
const _hoisted_11 = ["href"]
const _hoisted_12 = ["href"]
const _hoisted_13 = {
  key: 3,
  class: "eval-details"
}
const _hoisted_14 = ["href"]
const _hoisted_15 = ["href"]

import { ref, computed, onMounted } from 'vue'
import type { TableProps } from 'ant-design-vue'
import { vReveal } from '@/directives/reveal'

interface LeaderboardEntry {
  key: string
  rank: number
  model: string
  team: string
  score: number
  date: string
  paper?: string | null
  github?: string | null
  parameters?: string
  evaluationDetails?: {
    protocolAlignment?: number
    issueIdentification?: number
  }
}

interface ContributorEntry {
  key: string
  rank: number
  institution: string
  labs: string[]
  labCount: number
  protocolsCount?: number
  datasetsCount?: number
  contact?: string
  contributionDate?: string
  website?: string
  location?: string
}

type ActiveBoard = 'llm' | 'contributors'


export default /*@__PURE__*/_defineComponent({
  __name: 'LeaderboardSection',
  setup(__props) {

const searchQuery = ref('')
const leaderboardData = ref<LeaderboardEntry[]>([])
const contributorData = ref<ContributorEntry[]>([])
const sortKey = ref<string>('rank')
const sortOrder = ref<'ascend' | 'descend'>('ascend')
const activeBoard = ref<ActiveBoard>('llm')

const setBoard = (board: ActiveBoard) => {
  // Reset sort state when switching boards
  sortKey.value = 'rank'
  sortOrder.value = 'ascend'
  // Clear search when switching
  searchQuery.value = ''
  activeBoard.value = board
}

const columnsLlm = computed<TableProps['columns']>(() => [
  {
    title: 'Rank',
    dataIndex: 'rank',
    key: 'rank',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => a.rank - b.rank,
    width: 80,
    fixed: 'left' as const
  },
  {
    title: 'Model',
    dataIndex: 'model',
    key: 'model',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => a.model.localeCompare(b.model),
    width: 180
  },
  {
    title: 'Team',
    dataIndex: 'team',
    key: 'team',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => a.team.localeCompare(b.team),
    width: 160
  },
  {
    title: 'Score',
    dataIndex: 'score',
    key: 'score',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => a.score - b.score,
    width: 100
  },
  {
    title: 'Parameters',
    dataIndex: 'parameters',
    key: 'parameters',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => {
      const aVal = a.parameters || 'Unknown'
      const bVal = b.parameters || 'Unknown'
      return aVal.localeCompare(bVal)
    },
    width: 120
  },
  {
    title: 'Evaluation Details',
    dataIndex: 'evaluationDetails',
    key: 'evaluationDetails',
    width: 160
  },
  {
    title: 'Paper',
    dataIndex: 'paper',
    key: 'paper',
    width: 100
  },
  {
    title: 'Code',
    dataIndex: 'github',
    key: 'github',
    width: 100
  },
  {
    title: 'Date',
    dataIndex: 'date',
    key: 'date',
    sorter: (a: LeaderboardEntry, b: LeaderboardEntry) => 
      new Date(a.date).getTime() - new Date(b.date).getTime(),
    width: 120
  }
])

const columnsContrib = computed<TableProps['columns']>(() => [
  {
    title: 'Rank',
    dataIndex: 'rank',
    key: 'rank',
    sorter: (a: ContributorEntry, b: ContributorEntry) => a.rank - b.rank,
    width: 80,
    fixed: 'left' as const
  },
  {
    title: 'Institution',
    dataIndex: 'institution',
    key: 'institution',
    sorter: (a: ContributorEntry, b: ContributorEntry) => a.institution.localeCompare(b.institution),
    width: 180
  },
  {
    title: 'Location',
    dataIndex: 'location',
    key: 'location',
    sorter: (a: ContributorEntry, b: ContributorEntry) => (a.location || '').localeCompare(b.location || ''),
    width: 160
  },
  {
    title: 'Labs',
    dataIndex: 'labs',
    key: 'labs',
    customRender: ({ text }: any) => Array.isArray(text) ? text.join(', ') : text,
    sorter: (a: ContributorEntry, b: ContributorEntry) => (a.labs?.length || 0) - (b.labs?.length || 0),
    width: 200
  },
  {
    title: 'Lab Count',
    dataIndex: 'labCount',
    key: 'labCount',
    sorter: (a: ContributorEntry, b: ContributorEntry) => a.labCount - b.labCount,
    width: 100
  },
  {
    title: 'Protocols',
    dataIndex: 'protocolsCount',
    key: 'protocolsCount',
    sorter: (a: ContributorEntry, b: ContributorEntry) => (a.protocolsCount || 0) - (b.protocolsCount || 0),
    width: 100
  },
  {
    title: 'Datasets',
    dataIndex: 'datasetsCount',
    key: 'datasetsCount',
    sorter: (a: ContributorEntry, b: ContributorEntry) => (a.datasetsCount || 0) - (b.datasetsCount || 0),
    width: 100
  },
  {
    title: 'Contribution Date',
    dataIndex: 'contributionDate',
    key: 'contributionDate',
    sorter: (a: ContributorEntry, b: ContributorEntry) => 
      new Date(a.contributionDate || '').getTime() - new Date(b.contributionDate || '').getTime(),
    width: 140
  },
  {
    title: 'Contact',
    dataIndex: 'contact',
    key: 'contact',
    width: 180
  },
  {
    title: 'Website',
    dataIndex: 'website',
    key: 'website',
    width: 100
  }
])

const filteredLlmData = computed(() => {
  if (!searchQuery.value) {
    return leaderboardData.value
  }
  const query = searchQuery.value.toLowerCase()
  return leaderboardData.value.filter(item => 
    item.model.toLowerCase().includes(query) || 
    item.team.toLowerCase().includes(query)
  )
})

const filteredContributorData = computed(() => {
  if (!searchQuery.value) {
    return contributorData.value
  }
  const query = searchQuery.value.toLowerCase()
  return contributorData.value.filter(item =>
    item.institution.toLowerCase().includes(query) ||
    item.labs.join(', ').toLowerCase().includes(query)
  )
})

const handleSearch = () => {
  // 搜索功能已通过 computed 属性实现
}

const handleTableChange: TableProps['onChange'] = (pagination, filters, sorter: any) => {
  if (sorter.columnKey) {
    sortKey.value = sorter.columnKey
    sortOrder.value = sorter.order || 'ascend'
  }
}

const loadLeaderboardData = async () => {
  try {
    const response = await fetch('/leaderboard.json', { cache: 'no-store' })
    const data = await response.json()
    leaderboardData.value = data.map((item: any, index: number) => ({
      key: `${index}`,
      ...item
    }))
  } catch (error) {
    // 使用 fallback 数据
    leaderboardData.value = [
      {
        key: '0',
        rank: 1,
        model: 'LabOS VLM-235B',
        team: 'Stanford–Princeton',
        score: 4.21,
        date: '2025-10-29'
      },
      {
        key: '1',
        rank: 2,
        model: 'Gemini 2.5 Pro',
        team: 'Google DeepMind',
        score: 2.86,
        date: '2025-10-25'
      },
      {
        key: '2',
        rank: 3,
        model: 'GPT-4o',
        team: 'OpenAI',
        score: 2.02,
        date: '2025-10-24'
      }
    ]
  }
}

const loadContributorData = async () => {
  try {
    const response = await fetch('/contributors.json', { cache: 'no-store' })
    const data = await response.json()
    // Sort by total contributions (protocols + datasets) descending, then by lab count
    const sorted = data
      .map((item: any) => ({
        ...item,
        totalContributions: (item.protocolsCount || 0) + (item.datasetsCount || 0)
      }))
      .sort((a: any, b: any) => {
        if (b.totalContributions !== a.totalContributions) {
          return b.totalContributions - a.totalContributions
        }
        return b.labCount - a.labCount
      })
    contributorData.value = sorted.map((item: any, index: number) => ({
      key: `${index}`,
      rank: index + 1,
      ...item
    }))
  } catch (error) {
    // Fallback to static data
    const source = [
      { institution: 'Stanford', labs: ['CongLab', 'WuLab', 'SLab'] },
      { institution: 'Princeton', labs: ['WangLab', 'WuLab', 'DLab'] },
      { institution: 'Oregon State University', labs: ['LLab'] },
      { institution: 'University of Washington', labs: ['SLab'] }
    ]
    contributorData.value = source.map((item, index) => ({
      key: `${index}`,
      rank: index + 1,
      institution: item.institution,
      labs: item.labs,
      labCount: item.labs.length
    }))
  }
}

onMounted(() => {
  loadLeaderboardData()
  loadContributorData()
})

return (_ctx: any,_cache: any) => {
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_input_search = _resolveComponent("a-input-search")!
  const _component_a_table = _resolveComponent("a-table")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[5] || (_cache[5] = _createElementVNode("h2", { class: "section-title" }, "Leaderboard", -1)),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_a_button, {
            type: activeBoard.value === 'llm' ? 'primary' : 'default',
            size: "large",
            class: "switch-btn left",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (setBoard('llm')))
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode(" LLM Leaderboard ", -1)
            ]))]),
            _: 1
          }, 8, ["type"]),
          _createVNode(_component_a_button, {
            type: activeBoard.value === 'contributors' ? 'primary' : 'default',
            size: "large",
            class: "switch-btn right",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setBoard('contributors')))
          }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode(" Data Contributor Board ", -1)
            ]))]),
            _: 1
          }, 8, ["type"])
        ])
      ]),
      _createElementVNode("p", _hoisted_5, [
        (activeBoard.value === 'llm')
          ? (_openBlock(), _createElementBlock("span", _hoisted_6, "Latest evaluations on LSV. Click headers to sort; search by model or team."))
          : (_openBlock(), _createElementBlock("span", _hoisted_7, "Data contributors to the LabOS benchmark. Click headers to sort; search by institution or labs."))
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createVNode(_component_a_input_search, {
          value: searchQuery.value,
          "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((searchQuery).value = $event)),
          placeholder: activeBoard.value === 'llm' ? 'Search model or team…' : 'Search institution or labs…',
          class: "search-input",
          onSearch: handleSearch
        }, null, 8, ["value", "placeholder"])
      ]),
      _createElementVNode("div", _hoisted_9, [
        (_openBlock(), _createBlock(_component_a_table, {
          key: activeBoard.value,
          columns: activeBoard.value === 'llm' ? columnsLlm.value : columnsContrib.value,
          "data-source": activeBoard.value === 'llm' ? filteredLlmData.value : filteredContributorData.value,
          pagination: false,
          scroll: { x: activeBoard.value === 'llm' ? 1200 : 1400 },
          onChange: handleTableChange
        }, {
          bodyCell: _withCtx(({ column, record }) => [
            (column.key === 'score' && typeof record.score === 'number')
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(record.score.toFixed(2)), 1))
              : (column.key === 'paper' && record.paper)
                ? (_openBlock(), _createElementBlock("a", {
                    key: 1,
                    href: record.paper,
                    target: "_blank",
                    rel: "noopener noreferrer",
                    class: "link-cell"
                  }, " 📄 Paper ", 8, _hoisted_11))
                : (column.key === 'github' && record.github)
                  ? (_openBlock(), _createElementBlock("a", {
                      key: 2,
                      href: record.github,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      class: "link-cell"
                    }, " 🔗 Code ", 8, _hoisted_12))
                  : (column.key === 'evaluationDetails' && record.evaluationDetails)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                        _createElementVNode("div", null, "Protocol: " + _toDisplayString(record.evaluationDetails.protocolAlignment?.toFixed(1) || 'N/A'), 1),
                        _createElementVNode("div", null, "Issues: " + _toDisplayString(record.evaluationDetails.issueIdentification?.toFixed(1) || 'N/A'), 1)
                      ]))
                    : (column.key === 'website' && record.website)
                      ? (_openBlock(), _createElementBlock("a", {
                          key: 4,
                          href: record.website,
                          target: "_blank",
                          rel: "noopener noreferrer",
                          class: "link-cell"
                        }, " 🌐 Website ", 8, _hoisted_14))
                      : (column.key === 'contact' && record.contact)
                        ? (_openBlock(), _createElementBlock("a", {
                            key: 5,
                            href: `mailto:${record.contact}`,
                            class: "link-cell"
                          }, " ✉️ " + _toDisplayString(record.contact), 9, _hoisted_15))
                        : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["columns", "data-source", "scroll"]))
      ]),
      _cache[6] || (_cache[6] = _createElementVNode("p", { class: "note-text" }, " Scores follow a 0–5 human‑expert rubric for protocol alignment and issue identification, per the paper. ", -1))
    ])), [
      [_unref(vReveal)]
    ])
  ]))
}
}

})